import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Text } from '@src/design-system/text';
import { Input } from '@src/design-system/input';
import fileCheck from '@shared/assets/svgs/file-check.svg';
import { Edit2, Trash } from 'iconsax-react';

interface FilePreviewProps {
  file: File;
  onDelete: () => void;
  onRename: (newName: string) => void;
  showDelete?: boolean;
  autoEdit?: boolean; // ✅ new prop to control edit mode
}

export default function FilePreview({
  file,
  onDelete,
  onRename,
  showDelete = true,
  autoEdit = false,
}: FilePreviewProps) {
  const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  const fileExtension = file.name.split('.').pop();
  const [fileName, setFileName] = useState(fileNameWithoutExt);
  const [isEditing, setIsEditing] = useState(autoEdit);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onRename(`${fileName}.${fileExtension}`);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <div className='flex w-full min-w-0 flex-col gap-2 rounded-xl border border-primary-200 bg-primary-50 p-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <img
            src={fileCheck}
            className='size-10! shrink-0'
            alt=''
            aria-hidden='true'
          />

          {isEditing ? (
            <div className='flex min-w-0 flex-1 items-center gap-2'>
              <Input
                ref={inputRef}
                type='text'
                value={fileName}
                onChange={handleFileNameChange}
                variant='outline'
                className='min-w-0 flex-1 border-none p-0 selection:bg-gray-100 selection:text-primary-800 focus:border-none focus-within:border-none focus-within:ring-0'
              />
              <Check
                className='shrink-0 cursor-pointer text-primary-600 hover:text-primary-800'
                size={16}
                onClick={handleBlur}
              />
            </div>
          ) : (
            <div className='min-w-0 flex-1'>
              <div className='flex min-w-0 items-center gap-2'>
                <Text className='min-w-0 truncate text-base font-medium text-gray-800'>
                  {fileName}.{fileExtension}
                </Text>
                <Edit2
                  onClick={handleEditClick}
                  size='16'
                  className='shrink-0 cursor-pointer text-gray-700 hover:text-gray-700'
                  variant='TwoTone'
                />
              </div>
              <Text className='text-sm font-normal text-gray-800'>
                {(file.size / 1024 / 1024).toFixed(4)} MB
              </Text>
            </div>
          )}
        </div>

        {showDelete && (
          <Trash
            size='20'
            onClick={onDelete}
            className='shrink-0 cursor-pointer text-gray-800 hover:text-red-500'
            variant='Outline'
          />
        )}
      </div>
    </div>
  );
}
